// Room outline contract (CIN-15126, phase 0). Pure types (and one constant list), no imports: shared by the studio
// and the guest map.
//
// PLAN SPACE: millimeters, x to the audience's right, y up the plan toward the screen. The origin is the
// CENTER OF THE SCREEN WALL, so a room spans y in [-depth, 0]; no vertex may have y > 0.
//
// ORIENTATION: vertices run COUNTER-CLOCKWISE seen from above (y up). The interior is on the LEFT of every
// directed edge a -> b. `normalizeRoomGeometry` reverses clockwise input; `validateOutline` rejects it.
//
// EDGES: edge i runs from vertex i to vertex (i + 1) % n and is identified by its START vertex id. That id
// is what `screenEdge`, `RoomLevel.frontEdges` and (later) wall features anchor on.
//
// SCREEN EDGE CONVENTION: the screen edge is straight, lies on y = 0, is symmetric (a.x = -b.x) and is at
// least 2000 mm long. Counter-clockwise order therefore makes it run from +x to -x (a = (+w/2, 0),
// b = (-w/2, 0)): the rectangle is r1 (+hw, 0) -> r2 (-hw, 0) -> r3 (-hw, -d) -> r4 (+hw, -d), screenEdge 'r1'.
//
// BULGE (DXF convention): tan(sweep / 4) of the arc from a to b, positive = counter-clockwise arc. On a
// counter-clockwise outline a POSITIVE bulge bows OUTWARD (a curved back wall), a negative one inward.
// |bulge| <= 1, that is at most a semicircle.

/** A point in plan space, mm. */
export type PlanPoint = { x: number; y: number };

/** Axis-aligned box in plan space, mm. */
export type PlanRect = { minX: number; minY: number; maxX: number; maxY: number };

/** An item footprint: `width` along its local x, `depth` along local y, rotated clockwise (degrees) about (x, y). */
export type OutlineFootprint = { x: number; y: number; width: number; depth: number; rotation: number };

/** Plan mm. Vertices run counter-clockwise seen from above (plan y up, toward the screen). */
export type OutlineVertex = {
    /** Stable, at most 16 chars ('v1'...); walls, features and levels anchor on it. */
    id: string;
    /** Integer mm. */
    x: number;
    y: number;
    /** Edge from this vertex to the next: bulge = tan(sweep / 4); 0 or absent = straight. */
    bulge?: number;
};

export type ClosedPath = { vertices: OutlineVertex[] };

/** `screenEdge` is the id of the screen edge's start vertex. */
export type RoomOutline = ClosedPath & { screenEdge: string };

/** Anything the pure outline functions can measure; a level footprint has no screen edge. */
export type OutlineLike = ClosedPath & { screenEdge?: string };

export type RoomLevel = {
    id: string;
    name: string;
    /** mm, floor of the level above the main floor. */
    elevation: number;
    /** mm (soffit = elevation - slabThickness). */
    slabThickness: number;
    /** Footprint of the balcony slab, inside the room outline. */
    outline: ClosedPath;
    /** Vertex ids whose edge is the open front (gets the fascia, needs a balustrade). */
    frontEdges: string[];
};

export type RoomGeometry = {
    version: 1;
    /** null = implicit rectangle from width/depth. */
    outline: RoomOutline | null;
    /** The main floor is implicit; at most 3 extra levels. */
    levels: RoomLevel[];
};

/** `normalizeRoomGeometry` result: an outline that failed validation is dropped and flagged. */
export type NormalizedRoomGeometry = RoomGeometry & { invalid?: true };

export type ProfilePoint = { x: number; y: number; bulge?: number };

/** room_object_shape.profile: object-local mm (origin = footprint center, +y = the object's local front). */
export type ObjectProfile = {
    version: 1;
    path: { closed: boolean; points: ProfilePoint[] };
    /** Round-stage steps (offset rings). */
    steps?: { count: number; tread: number; edges: 'front' | 'all' | number[] };
    /** Barrier gaps (mm along the polyline): a center stair through a barrier. */
    openings?: { at: number; width: number }[];
    /** Barrier/railing thickness, mm. */
    thickness?: number;
    cap?: 'none' | 'handrail' | 'brass' | 'glass';
    led?: 'none' | 'top' | 'base' | 'face';
    /** Stage front riser (ventilation grilles). */
    riser?: 'plain' | 'grille' | 'led';
    /** Counter: distance between cupholders along the path, mm (300..3,000; default 900). */
    cupholderSpacing?: number;
    /** Counter: `false` leaves out the cupholder rings and the grilles (a bar). Absent = true. */
    cupholders?: boolean;
    /**
     * CIN-15200: the object's own material (table top, counter, pillar, stage, wall, screen bezel). Absent = the
     * kind's default look. `texture` is one of the room's procedural textures, never `image`.
     */
    color?: `#${string}`;
    texture?: ObjectTexture;
    /**
     * Screen: a frame around the picture, mm: `width` beyond each edge (20..1,500), `depth` toward the audience
     * (10..600). `left`/`right`/`top` (0..1,500) and `bottom` (0..5,000, down to the floor) override `width` per side; 0 leaves that side unframed.
     */
    bezel?: { width: number; depth: number; left?: number; right?: number; top?: number; bottom?: number };
    /** Stairs: which side rails are fitted, relative to the climb direction (up the steps). Absent = `both`. */
    rails?: 'both' | 'left' | 'right' | 'none';
};

export const BEZEL_SIDES = ['left', 'right', 'top', 'bottom'] as const;
export const OBJECT_RAILS = ['both', 'left', 'right', 'none'] as const;

/** Textures an object can wear: the room's procedural textures (room-design.ts `TEXTURE_IDS`) without `image`. */
export const OBJECT_TEXTURES = [
    'plain',
    'velvet',
    'fabric-weave',
    'leather',
    'wood-panels',
    'parquet',
    'concrete',
    'acoustic-panels',
    'wood-slats',
    'brick',
    'marble'
] as const;
export type ObjectTexture = (typeof OBJECT_TEXTURES)[number];

export type OutlineEdge = {
    /** Id of the start vertex. */
    id: string;
    index: number;
    a: PlanPoint;
    b: PlanPoint;
    bulge: number;
    /** Straight distance a -> b. */
    chord: number;
    /** Arc length for a curved edge, else the chord. */
    length: number;
    isScreen: boolean;
    /** Unit vector pointing into the room. For an arc it is the CHORD's inward normal; use `nearestWall` for the local one. */
    normal: PlanPoint;
};

export type OutlineError =
    | 'too-few'
    | 'too-many'
    | 'short-edge'
    | 'self-intersect'
    | 'no-screen-edge'
    | 'screen-edge-curved'
    | 'screen-edge-off-wall'
    | 'screen-edge-asymmetric'
    | 'screen-edge-short'
    | 'beyond-screen-wall'
    | 'bulge-range'
    | 'too-small'
    | 'too-large'
    | 'level-outside-room'
    | 'level-elevation'
    // Additions to the plan's list, for cases it names but gives no code:
    /** Non-integer or non-finite coordinate, bad or duplicate vertex id. */
    | 'bad-vertex'
    /** Vertices run clockwise. */
    | 'not-ccw'
    /** A level's `frontEdges` names a vertex that does not exist. */
    | 'level-front-edge'
    /** Object profile fields (steps, openings, thickness) out of range. */
    | 'bad-profile'
    /** An edit touched the screen wall (curve, split, delete or move it), which the studio keeps fixed. */
    | 'protected';
